import React from 'react'
import { debouncedLoadCustomers } from 'utils/optionsLoaders'
import { useSessionState } from 'contexts/Session'
import {
  INITIAL_SORT_STATE,
  UPLOAD_REQUEST_TABLE_NAME,
  UPLOAD_STATUS_STYLES,
  PRIORITY_OPTIONS,
} from './utils/constants'
import debounce from 'utils/debounce'
import { humanizeSnakeCase } from 'utils/format'
import {
  Grid,
  Typography,
  TypeaheadMultipleSelect,
  TypeaheadSingleSelect,
  SideDrawerWrapper,
  TextField,
  Box,
} from '@the-platform-group/component-library'
import { DataGrid } from '@the-platform-group/component-library/DataGrid'
import { DataGridApiProvider } from '@the-platform-group/component-library/DataGrid/DataGridApiContext'
import { AsyncSelect } from '@the-platform-group/component-library/Select'
import { ExportButton } from '@the-platform-group/component-library/Button'
import { handleSortModelChange } from 'components/Table/utils'
import OverdueBanner from './components/OverdueBanner'
import { getColumnValues, formatRowValues } from './utils/getTableValues'
import useRequestFormFilters from 'pages/IrwinAssistant/Requests/hooks/useRequestFormFilters'
import useRequestFormMutations from 'pages/IrwinAssistant/Requests/hooks/useRequestFormMutations'
import DeleteRequestModal from './components/DeleteRequestModal'
import { Button } from '@the-platform-group/component-library/Button'
import CreateRequestModal from './components/CreateRequestModal'
import EditRequestModal from './components/EditRequestModal'
import ManageRequestModal from './components/ManageRequestModal'
import { NO_ASSIGNEE_OPTION } from './utils/constants'
import CustomerDetailPanel from './components/CustomerDetailPanel'

const UploadRequests = () => {
  // Filters hook - handles table filtering, pagination, and data fetching
  const {
    STATUS_OPTIONS,
    page,
    params,
    uploadRequests,
    itemCount,
    overdueCount,
    assignees,
    isFetching,
    isDownloading,
    queryParams,
    // Filter handlers
    handleCSVDownload,
    handleOnSort,
    handlePageChange,
    handlePageSizeChange,
    handleCustomerChange,
    handleStatusChange,
    handleNameChange,
    handleAssigneeChange,
    handleOverdueChange,
    handlePriorityChange,
  } = useRequestFormFilters()

  // Mutations hook - handles CRUD operations and modal state management
  const {
    // Table row operations
    updateUserUploadRequest,
    handleFileDownload,
    // Assistant login handlers
    handleActionLogin,
    // Create modal state and handlers
    createModalState,
    handleCreateModalOpen,
    handleCreateModalClose,
    // Edit modal state and handlers
    editModalState,
    handleEditModalOpen,
    handleEditModalClose,
    // Manage modal state and handlers
    manageModalState,
    handleManageModalOpen,
    handleManageModalClose,
    // Delete modal state and handlers
    deleteModalState,
    handleDeleteModalOpen,
    handleDeleteModalClose,
    handleDeleteConfirm,
    isDeleting,
  } = useRequestFormMutations(params, uploadRequests)

  const { pageSize, customers, assignee, overdue } = queryParams
  const { currentUser } = useSessionState()

  const debouncedRequestNameSearch = debounce(handleNameChange, 250)

  return (
    <SideDrawerWrapper headerProps={{ sx: { pl: 2.5, pb: 0 } }}>
      <DataGridApiProvider>
        <Box
          sx={{
            height: 'calc(100vh - 112px)', // subtracting navbar and header height
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flexShrink: 0 }}>
            <Grid container spacing={3} sx={{ flexGrow: 1 }}>
              <Grid item xs={5}>
                <Grid container justifyContent="space-between" sx={{ py: 1 }}>
                  <Typography variant="h4">Irwin Assistant Requests</Typography>
                </Grid>
              </Grid>
              <Grid item xs justify="flex-end">
                <Button onClick={handleCreateModalOpen} sx={{ float: 'right' }}>
                  Create request for client
                </Button>
              </Grid>

              <Grid item>
                <ExportButton
                  label="Export CSV"
                  options={[
                    {
                      onClick: getExportableColumns => handleCSVDownload(getExportableColumns),
                    },
                  ]}
                  handleClick={(option, getExportableColumns) =>
                    option.onClick(getExportableColumns)
                  }
                  isLoading={isDownloading}
                />
              </Grid>
            </Grid>

            {overdueCount > 0 ? (
              <OverdueBanner
                overdue={overdue}
                overdueCount={overdueCount}
                handleOverdueChange={handleOverdueChange}
              />
            ) : null}
            <Grid container spacing={2} alignItems="center">
              <Grid item md={3} sm={6} xs={12}>
                <AsyncSelect
                  fullWidth
                  hideArrow
                  hideOverflow
                  includeClearAll
                  isMulti
                  value={customers || []}
                  variant="outlined"
                  placeholder="Search customers"
                  onChange={handleCustomerChange}
                  optionsLoader={term => debouncedLoadCustomers({ term, state: 'active' })}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              </Grid>
              <Grid item md={3} sm={6} xs={12}>
                <TextField
                  fullWidth
                  isSearchInput
                  clearable
                  placeholder="Search by request name"
                  onChange={e => debouncedRequestNameSearch(e.target.value)}
                  sx={{ mt: 0.5 }} // fix alignment issues
                />
              </Grid>
              <Grid item md={2} sm={3} xs={12}>
                <TypeaheadMultipleSelect
                  fullWidth
                  includeClearAll
                  hideOverflow
                  showCheckBox
                  disableCloseOnSelect
                  options={STATUS_OPTIONS}
                  onChange={(_, statuses) => handleStatusChange(statuses)}
                  defaultValue={STATUS_OPTIONS.filter(opt => opt.value !== 'completed')}
                  variant="outlined"
                  id="status-filter"
                  label="Select Status"
                />
              </Grid>
              <Grid item md={2} sm={3} xs={12}>
                <TypeaheadSingleSelect
                  includeClearAll
                  label="Filter by assignee"
                  variant="outlined"
                  options={[
                    NO_ASSIGNEE_OPTION,
                    ...assignees.map(assignee => ({
                      label: assignee.fullName,
                      value: assignee.id,
                    })),
                  ]}
                  onChange={(_, assignee) => handleAssigneeChange(assignee)}
                  value={assignee}
                  handleClear={() => handleAssigneeChange({})}
                />
              </Grid>
              <Grid item md={2} sm={3} xs={12}>
                <TypeaheadMultipleSelect
                  fullWidth
                  includeClearAll
                  hideOverflow
                  disableCloseOnSelect
                  showCheckBox
                  options={Object.keys(PRIORITY_OPTIONS).map(priority => ({
                    label: humanizeSnakeCase(priority),
                    value: priority,
                  }))}
                  onChange={(_, priorities) => handlePriorityChange(priorities)}
                  variant="outlined"
                  label="Filter by priority"
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ flexGrow: 1, minHeight: 0 }}>
            <DataGrid
              name={UPLOAD_REQUEST_TABLE_NAME}
              rows={formatRowValues(uploadRequests)}
              columns={getColumnValues({
                currentUser,
                handleFileDownload,
                updateUserUploadRequest,
                handleManageModalOpen,
                handleEditModalOpen,
                handleActionLogin,
                handleDeleteModalOpen,
              })}
              sx={{ ...UPLOAD_STATUS_STYLES }}
              loading={isFetching}
              autoHeight={false}
              getRowHeight={obj => obj.densityFactor * 120}
              onSortModelChange={sortModel => handleSortModelChange(sortModel, handleOnSort, false)}
              initialState={{
                sorting: {
                  sortModel: [INITIAL_SORT_STATE],
                },
                columns: {
                  columnVisibilityModel: {
                    security: false,
                    userName: false,
                    userEmail: false,
                  },
                },
                pinnedColumns: { right: ['actions'] },
              }}
              getDetailPanelHeight={() => 'auto'}
              getDetailPanelContent={({ row }) => <CustomerDetailPanel customer={row.customer} />}
              // pagination
              rowCount={itemCount}
              pageSize={pageSize}
              page={page}
              pageSizeOptions={[50, 100, 150, 200]}
              onPageSizeChange={handlePageSizeChange}
              onPageChange={handlePageChange}
            />
          </Box>
        </Box>
      </DataGridApiProvider>

      <DeleteRequestModal
        open={deleteModalState.open}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        requestName={deleteModalState.requestToDelete?.requestName || ''}
        isLoading={isDeleting}
      />
      <CreateRequestModal open={createModalState.open} onClose={handleCreateModalClose} />
      <EditRequestModal
        open={editModalState.open}
        onClose={handleEditModalClose}
        request={editModalState.requestToEdit}
      />
      <ManageRequestModal
        open={manageModalState.open}
        onClose={handleManageModalClose}
        request={manageModalState.requestToManage}
      />
    </SideDrawerWrapper>
  )
}

export default UploadRequests
